import { useContext, useState } from "react";
import { Link } from "react-router-dom";
import { DataTable } from "primereact/datatable";
import { sendMarketingCampaign } from "../../../api/marketingCampaigns.api";
import { CampaignSendEmailPopUp } from "./CampaignSendEmailPopUp";
import { UserContext } from "../../../components/Context/AuthUser";
import { Column } from "primereact/column";
import { ColumnGroup } from "primereact/columngroup";
import { MultiSelect } from "primereact/multiselect";
import { Row } from "primereact/row";
import { Tag } from "primereact/tag";
import { Dropdown } from "primereact/dropdown";
import { Checkbox } from "primereact/checkbox";
import { ZonesToolTip } from "./ZonesToolTip";

export default function MarketingCampaignTable({
  contacts,
  contactsToSend,
  setContactsToSend,
  openSendMarketingCampaignEmail,
  handleOpenSendMarketingCampaignEmail,
  selectedMarketingCampaign,
  filters,
  setFilters,
  tags,
  fetchGetContactsByEmailNotifications,
  lazyParams,
  totalRecords,
  onPage,
  loading,
  selectAllFilteredContacts,
  consultants,
  allZones,
}) {
  const { user } = useContext(UserContext);

  const [hoverData, setHoverData] = useState({
    zones: [],
    visible: false,
    color: "#6366f1",
    title: "",
  });
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  const consultantFilterTemplate = () => {
    return (
      <div className="flex flex-column gap-2 p-2" style={{ minWidth: "200px" }}>
        <MultiSelect
          value={filters.consultant.value}
          options={consultants}
          optionLabel="fullName"
          onChange={async (e) => {
            setFilters((prev) => ({
              ...prev,
              consultant: { ...prev.consultant, value: e.value },
            }));

            const selectedConsultantIds = e.value
              ? e.value.map((c) => c._id)
              : [];

            await fetchGetContactsByEmailNotifications(
              null,
              null,
              null,
              null,
              1,
              null,
              null,
              selectedConsultantIds,
            );
          }}
          placeholder="Filtrar Consultores"
          className="p-column-filter w-full"
          maxSelectedLabels={1}
          filter
        />
      </div>
    );
  };

  const renderZoneFilter = (filterKey) => (
    <div className="p-2" style={{ minWidth: "250px" }}>
      <MultiSelect
        value={filters[filterKey].value}
        options={allZones}
        optionLabel="name"
        // Propiedades de Agrupación
        optionGroupLabel="label"
        optionGroupChildren="items"
        filter
        placeholder={`Zonas ${filterKey === "adZones" ? "Ads" : "Req"}`}
        className="p-column-filter w-full"
        display="chip"
        onChange={async (e) => {
          const newFilters = { ...filters };
          newFilters[filterKey].value = e.value;
          setFilters(newFilters);

          const adZoneIds = newFilters.adZones.value?.map((z) => z._id) || [];
          const reqZoneIds = newFilters.reqZones.value?.map((z) => z._id) || [];

          await fetchGetContactsByEmailNotifications(
            null,
            null,
            null,
            null,
            1,
            null,
            null,
            null,
            adZoneIds,
            reqZoneIds,
          );
        }}
      />
    </div>
  );

  const adZoneFilterTemplate = () => renderZoneFilter("adZones");
  const reqZoneFilterTemplate = () => renderZoneFilter("reqZones");

  const contactTypeFilterTemplate = () => {
    const types = ["Cliente", "Propietario"];
    const operatorOptions = [
      { label: "Cualquiera (OR)", value: "or" },
      { label: "Todos (AND)", value: "and" },
    ];

    return (
      <div className="flex flex-column gap-2 p-2" style={{ minWidth: "150px" }}>
        <MultiSelect
          value={filters.contactType.value}
          options={types}
          onChange={async (e) => {
            setFilters((prev) => ({
              ...prev,
              contactType: { ...prev.contactType, value: e.value },
            }));
            const currentTagIds = filters.tags.value?.map((t) => t._id) || [];
            await fetchGetContactsByEmailNotifications(
              currentTagIds,
              e.value,
              filters.contactType.operator,
            );
          }}
          placeholder="Elegir Tipo"
          className="p-column-filter w-full"
          maxSelectedLabels={1}
        />
        <Dropdown
          value={filters.contactType.operator}
          options={operatorOptions}
          onChange={async (e) => {
            setFilters((prev) => ({
              ...prev,
              contactType: { ...prev.contactType, operator: e.value },
            }));
            const currentTagIds = filters.tags.value?.map((t) => t._id) || [];
            const currentTypes = filters.contactType.value || [];
            await fetchGetContactsByEmailNotifications(
              currentTagIds,
              currentTypes,
              e.value,
            );
          }}
          style={{ height: "30px", fontSize: "11px" }}
          className="w-full"
        />
      </div>
    );
  };

  const onHeaderCheckboxChange = () => {
    if (contactsToSend.length === totalRecords && totalRecords > 0) {
      setContactsToSend([]);
    } else {
      selectAllFilteredContacts();
    }
  };

  const headerCheckboxTemplate = () => (
    <Checkbox
      onChange={onHeaderCheckboxChange}
      checked={contactsToSend.length === totalRecords && totalRecords > 0}
    />
  );

  // Template base reutilizable para las dos columnas de zonas
  const zonesBaseTemplate = (zones, color, title) => {
    const maxVisible = 2;
    const hasMore = zones.length > maxVisible;
    const visibleZones = zones.slice(0, maxVisible);
    return (
      <div
        className="flex flex-wrap align-items-center gap-2"
        style={{
          minWidth: "120px",
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          gap: "2px",
        }}
      >
        {visibleZones.map((z, index) => (
          <div
            key={index}
            className="flex flex-column align-items-center"
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              backgroundColor: color,
              color: "white",
              borderRadius: "6px",
              padding: "2px 8px",
              minWidth: "70px",
              textAlign: "center",
            }}
          >
            <span
              style={{ fontSize: "9px", fontWeight: "bold", lineHeight: "1.2" }}
            >
              {z.name}
            </span>
            <span
              style={{
                fontSize: "7px",
                opacity: "0.9",
                textTransform: "uppercase",
                letterSpacing: "0.2px",
              }}
            >
              {z.label || "Others"}
            </span>
          </div>
        ))}
        {hasMore && (
          <Tag
            value={`+${zones.length - maxVisible}`}
            onMouseEnter={(e) => {
              setHoverData({ zones, visible: true, color, title });
              setMousePos({ x: e.clientX, y: e.clientY });
            }}
            onMouseLeave={() =>
              setHoverData((prev) => ({ ...prev, visible: false }))
            }
            style={{
              fontSize: "8px",
              cursor: "pointer",
              backgroundColor: "#e2e8f0",
              color: "#475569",
            }}
          />
        )}
      </div>
    );
  };

  const adZonesBodyTemplate = (rowData) =>
    zonesBaseTemplate(
      rowData.adZones || [],
      "#6366f1",
      "Zonas Propietario (Ads)",
    );
  const reqZonesBodyTemplate = (rowData) =>
    zonesBaseTemplate(
      rowData.requestZones || [],
      "#10b981",
      "Zonas Cliente (Requests)",
    );

  let headerGroup = (
    <ColumnGroup>
      <Row>
        <Column
          header={headerCheckboxTemplate}
          headerStyle={{ width: "1%" }}
          rowSpan={2}
        />
        <Column
          header="Nombre"
          rowSpan={2}
          field="fullName"
          style={{ width: "10%" }}
        />
        <Column
          header="Email"
          rowSpan={2}
          field="email"
          style={{ width: "10%" }}
        />
        <Column
          header="Empresa"
          rowSpan={2}
          field="company"
          style={{ width: "10%" }}
        />
        <Column
          header="Consultor"
          rowSpan={2}
          style={{ width: "10%" }}
          filter
          filterElement={consultantFilterTemplate}
          showFilterMatchModes={false}
          showFilterOperator={false}
          showAddButton={false} // Eliminamos "Add Rule"
          showApplyButton={false} // Eliminamos "Apply"
          showClearButton={false} // Eliminamos "Clear" nativo
        />
        <Column
          header="Zonas Propietario (Ads)"
          rowSpan={2}
          style={{ width: "12%" }}
          body={adZonesBodyTemplate}
          filter
          filterElement={adZoneFilterTemplate}
          showFilterMatchModes={false}
          showFilterOperator={false}
          showAddButton={false} // Eliminamos "Add Rule"
          showApplyButton={false} // Eliminamos "Apply"
          showClearButton={false} // Eliminamos "Clear"
        />
        <Column
          header="Zonas Cliente (Req)"
          rowSpan={2}
          style={{ width: "12%" }}
          body={reqZonesBodyTemplate}
          filter
          filterElement={reqZoneFilterTemplate}
          showFilterMatchModes={false}
          showFilterOperator={false}
          showAddButton={false} // Eliminamos "Add Rule"
          showApplyButton={false} // Eliminamos "Apply"
          showClearButton={false} // Eliminamos "Clear"
        />
        <Column
          header="Tipo"
          rowSpan={2}
          style={{ width: "10%" }}
          field="tag"
          filter
          filterElement={contactTypeFilterTemplate}
          showFilterMatchModes={false}
          showFilterOperator={false}
          showAddButton={false} // Eliminamos "Add Rule"
          showApplyButton={false} // Eliminamos "Apply"
          showClearButton={false} // Eliminamos "Clear"
        />
      </Row>
    </ColumnGroup>
  );

  const contactTypeBodyTemplate = (rowData) => (
    <div className="flex gap-1" style={{ display: "flex", gap: "4px" }}>
      {rowData.tag &&
        rowData.tag.map((t, index) => (
          <Tag
            key={index}
            value={t}
            severity={t === "Cliente" ? "info" : "warning"}
          />
        ))}
    </div>
  );

  const onSelectionChange = (e) => {
    if (e.type === "all") {
      onHeaderCheckboxChange();
    } else {
      setContactsToSend(e.value);
    }
  };

  const isSelectionOutofSync = contactsToSend.length !== totalRecords;
  const showSyncButton =
    contactsToSend.length > 0 && isSelectionOutofSync && totalRecords > 0;

  return (
    <div className="LayoutMarketingCampaigns__contain__children__contactsTable">
      {hoverData.visible && (
        <ZonesToolTip mousePos={mousePos} hoverData={hoverData} />
      )}

      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          gap: "10px",
          marginBottom: "10px",
        }}
      >
        <p className="LayoutMarketingCampaigns__contain__children__contactsTable__title m-0">
          Has seleccionado <strong>{contactsToSend.length}</strong> contactos.
        </p>
        {showSyncButton && (
          <button
            onClick={selectAllFilteredContacts}
            className="p-button p-component p-button-outlined p-button-sm"
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              borderRadius: "6px",
              padding: "8px 16px",
              cursor: "pointer",
              border: "1px solid #2B363D",
              backgroundColor: "#2B363D",
              color: "white",
              fontWeight: "600",
            }}
          >
            <i className="pi pi-sync"></i>
            Igualar selección con filtros de la tabla ({totalRecords})
          </button>
        )}
      </div>

      <DataTable
        selectAll={contactsToSend.length === totalRecords && totalRecords > 0}
        selectionPageOnly={false}
        loading={loading}
        value={contacts}
        lazy
        paginator
        first={lazyParams.first}
        rows={lazyParams.rows}
        totalRecords={totalRecords}
        onPage={onPage}
        rowsPerPageOptions={[10, 25, 50]}
        removableSort
        responsiveLayout="scroll"
        resizableColumns
        dataKey="_id"
        selectionMode="checkbox"
        selection={contactsToSend}
        onSelectionChange={onSelectionChange}
        columnResizeMode="fit"
        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
        headerColumnGroup={headerGroup}
        emptyMessage="No se ha encontrado ningún contacto"
      >
        <Column
          selectionMode="multiple"
          headerStyle={{ width: "1%" }}
          headerSelectionMode="all"
        />
        <Column
          field="fullName"
          body={(ev) => <Link to={`/contactos/${ev._id}`}>{ev.fullName}</Link>}
        />
        <Column field="email" />
        <Column field="company" />

        <Column
          field="consultant.fullName"
          header="Asesor"
          body={(rowData) => (
            <div style={{ display: "flex", flexDirection: "column" }}>
              <span className="font-bold">
                {rowData.consultant?.fullName || "Sin asignar"}
              </span>
              {rowData.consultant?.consultantEmail && (
                <small
                  className="text-secondary"
                  style={{ fontSize: "0.8rem" }}
                >
                  {rowData.consultant.consultantEmail}
                </small>
              )}
            </div>
          )}
        />

        <Column
          header="Zonas Propietario (Ads)"
          body={adZonesBodyTemplate}
          style={{ width: "12%" }}
        />
        <Column
          header="Zonas Cliente (Req)"
          body={reqZonesBodyTemplate}
          style={{ width: "12%" }}
        />
        <Column field="tag" body={contactTypeBodyTemplate} />
      </DataTable>

      {openSendMarketingCampaignEmail && (
        <CampaignSendEmailPopUp
          handleOpenSendMarketingCampaignEmail={
            handleOpenSendMarketingCampaignEmail
          }
          selectedMarketingCampaign={selectedMarketingCampaign}
          contactsToSend={contactsToSend}
          sendMarketingCampaign={sendMarketingCampaign}
          user={user}
        />
      )}
    </div>
  );
}
